import { z } from "zod";

// Helper library to make HTTP requests that conform to the RequestEndpoint interface and Hono input validations.

export type StringCastable = string | number | boolean | Date | null;
export type Method =
  "get" | "post" | "put" | "delete" | "patch" | "options" | "head";

export type JsonValueInput =
  | Date
  | string
  | number
  | boolean
  | null
  | { [key: string]: JsonValueInput }
  | JsonValueInput[];

export interface RequestEndpoint {
  method: Method;
  path: string;
  // compatible with the ValidationTargets type from hono after castValue function is applied.
  input: {
    param?: Record<string, StringCastable>;
    // Hono parses ?tags=a as { tags: "a" }
    // ?tags=a&tags=b is parsed as { tags: ["a", "b"] }
    // ?tags=[] => { tags: [] }
    query?: Record<string, StringCastable | StringCastable[]>;
    header?: Record<string, StringCastable> | undefined;
    json?: JsonValueInput;
    form?: Record<string, StringCastable | File | (StringCastable | File)[]>;
    cookie?: Record<string, Exclude<StringCastable, Date>> | undefined;
  };
  output: { status: number; body: unknown; headers: Record<string, string> };
}

type Logger = {
  debug: (message: string, ...args: unknown[]) => void;
  warn: (message: string, ...args: unknown[]) => void;
  error: (message: string, ...args: unknown[]) => void;
};

export interface RequestContext {
  host: string;
  getBearerToken: (
    method: Method,
    path: string,
  ) => string | null | Promise<string | null>;
  /**
   * Static headers attached at client construction. Merged first; can be
   * overridden by getHeaders, the bearer token, content-type, and per-call
   * input.header (in that order — see buildRequest).
   */
  headers?: Record<string, string>;
  /**
   * Per-request dynamic headers, evaluated alongside getBearerToken. Use this
   * when the header value depends on runtime state that isn't known at client
   * construction time (e.g. dashboard impersonation, feature-flag opt-ins).
   * Merged after `headers` so dynamic values win.
   */
  getHeaders?: (
    method: Method,
    path: string,
  ) => Record<string, string> | Promise<Record<string, string>>;
  logger?: Logger;
  /**
   * Plain label used in dev-console log lines (rendered as `[<prefix>]` in
   * magenta). Defaults to "req". Callers identify their own client here
   * (e.g. "api") so the shared request library stays backend-agnostic.
   */
  logPrefix?: string;
  onRequestError?: (requestError: RequestError) => void;
  onRequestSuccess?: (request: {
    context: RequestContext;
    method: RequestEndpoint["method"];
    path: RequestEndpoint["path"];
    input: RequestArgs<RequestEndpoint>[0];
    output: RequestOutput<RequestEndpoint>;
    durationMs: number;
    debugData: { [k: string]: unknown };
  }) => void;
  onRequestStart?: (request: {
    context: RequestContext;
    method: RequestEndpoint["method"];
    path: RequestEndpoint["path"];
    options: RequestArgs<RequestEndpoint>[0];
    debugData: { [k: string]: unknown };
  }) => void;
  fetch?: (input: Request, init: RequestInit) => Promise<Response>;
}

export interface RequestOptions {
  signal?: AbortSignal;
  credentials?: "include" | "same-origin" | "omit";
  /**
   * Opt out of `parseBody`. Returns the raw `response.body` (a
   * `ReadableStream<Uint8Array> | null`) in the typed output's `body` slot,
   * so callers can pipe end-to-end (SSE, NDJSON, large binaries) without
   * buffering the whole response into memory on the api process.
   *
   * The api should be JSON in / JSON out on its own request path —
   * `.text()` / `.blob()` on a multi-MB upstream response freezes the
   * Node event loop for tens of ms during JSON.parse / Blob assembly,
   * which on a `--cpu 1` Cloud Run container stalls every other in-flight
   * request. `stream: true` keeps the bytes flowing without paying that
   * CPU cost.
   *
   * The OpenAPI-declared body type still wins at compile time (typically
   * `null` for streaming endpoints) — at runtime the body is the raw
   * stream; consumers normalize it through `extractStreamFromBody`.
   */
  stream?: boolean;
}

export type RequestArgs<E extends RequestEndpoint> =
  IsEmptyObject<E["input"]> extends true
    ? [arg?: E["input"] & RequestOptions]
    : [arg: E["input"] & RequestOptions];

export type RequestOutput<E extends RequestEndpoint> = Exclude<
  E["output"],
  { status: HandledClientErrorCode | HandledServerErrorCode }
>;

export const EMPTY_QUERY_PARAM_ARRAY_VALUE = "[]";
export const EMPTY_FORM_ARRAY_VALUE = "[]";
export const NULL_STR = "null";
export const TRUE_STR = "true";
export const FALSE_STR = "false";

// const { status, body } = await request<{
//   method: "delete";
//   path: "/internal/e2e/prisma-delete-test";
//   input: Record<never, never>;
//   output: { status: 204; body: null; headers: Record<string, string> };
// }>(
//   { host: "https://api.example.com", getToken: (method, path) => null },
//   "delete",
//   "/internal/e2e/prisma-delete-test",
// );
// console.log(status, body);
const emptyOptions: Record<string, never> = Object.freeze({});
export default async function request<E extends RequestEndpoint>(
  context: RequestContext,
  basePath: string,
  method: E["method"],
  path: E["path"],
  ...args: RequestArgs<E>
): Promise<RequestOutput<E>> {
  const options = args[0] ?? emptyOptions;
  const { response, debugData, startTime } = await requestResponse(
    context,
    basePath,
    method,
    path,
    options,
  );

  const responseStatus = response.status;
  const responseHeaders: Record<string, string> = Object.fromEntries(
    response.headers,
  );
  debugData["responseContentType"] = response.headers.get("content-type");

  let responseBody: unknown;
  if (options.stream === true) {
    // Caller wants the raw stream — do not consume the body here.
    responseBody = response.body;
  } else {
    try {
      responseBody = await parseBody(response);
    } catch (error) {
      if (isAbortError(error))
        throw raiseAbortError(context, error, { method, path, debugData });
      const requestError = new RequestError(
        `cannot parse response body, ${error instanceof Error ? error.message : String(error)}`,
        { cause: error, debugData, method, path },
      );
      context.onRequestError?.(requestError);
      logRequestError(context, requestError);
      throw requestError;
    }
  }

  const safeResponseBody =
    responseBody === null
      ? null
      : typeof responseBody === "string"
        ? snip(responseBody, 200)
        : responseBody;
  debugData["responseBody"] = safeResponseBody;

  const durationMs = Date.now() - startTime;
  // oxlint-disable-next-line typescript/consistent-type-assertions -- Ben approved this pre-existing transport assertion.
  const requestOutput = {
    body: responseBody,
    status: responseStatus,
    headers: responseHeaders,
  } as RequestOutput<E>;

  context.onRequestSuccess?.({
    context,
    method,
    path,
    input: options,
    output: requestOutput,
    durationMs,
    debugData,
  });
  logRequestSuccess(context, method, path, requestOutput, durationMs);

  return requestOutput;
}

/**
 * Shared HTTP pipeline: bearer token, build, fetch with retries, status
 * classification (HANDLED_CLIENT/SERVER → ClientError/ServerError, 1xx/3xx →
 * StatusError). Fires onRequestError + logRequestError on failures, and
 * onRequestStart per attempt.
 *
 * On 2xx success, returns the raw Response — the caller owns body
 * consumption and the onRequestSuccess / logRequestSuccess hooks (success
 * is caller-defined: parsed body for JSON, response received for streams).
 */
export async function requestResponse(
  context: RequestContext,
  basePath: string,
  method: Method,
  path: string,
  options: RequestEndpoint["input"] & RequestOptions,
): Promise<{
  response: Response;
  debugData: { [k: string]: unknown };
  startTime: number;
}> {
  // The browser's own default: cookies go to the page's own host only. A
  // sandbox SPA calls the api on its own host (front-config.ts
  // resolveApiUrl), and Cloudflare Access needs its cookie there; calls to
  // another host (staging, production, local dev) still carry none.
  const { signal, stream, credentials = "same-origin", ...input } = options;
  // Default to a 60 s deadline on every non-streaming call so a hung
  // upstream doesn't tie up a Cloud Run request slot until Cloud Run's
  // edge timeout (300 s) does. Streaming endpoints opt out (the caller
  // owns the connection lifecycle there). Callers can pass their own
  // `signal` to extend the deadline (slow PDF parses) or shorten it.
  const effectiveSignal =
    signal === undefined && stream !== true
      ? AbortSignal.timeout(60_000)
      : signal;
  const {
    host,
    getBearerToken,
    getHeaders,
    headers: contextHeaders,
    onRequestStart,
    onRequestError,
  } = context;

  // The token is asked again before every attempt, never reused across
  // retries. A retry can run long after the first attempt: a native webview
  // suspended in the background freezes the request, and on resume the dead
  // socket fails and retries. The token read before the suspension has expired
  // by then, and the api answered the retry with a 401 for a session that was
  // still valid. `getBearerToken` returns the cached token while it is fresh,
  // so asking again costs nothing on the usual path. The headers stay those of
  // the first attempt: they carry request context (the dashboard's selected
  // organisation), and a retry must not move a write to another context.
  async function acquireAuthorization(): Promise<
    [string | null, Record<string, string>]
  > {
    try {
      return await Promise.all([
        getBearerToken(method, path),
        getHeaders === undefined
          ? Promise.resolve({})
          : getHeaders(method, path),
      ]);
    } catch (error) {
      if (isAbortError(error))
        throw raiseAbortError(context, error, { method, path });
      // A browser network blip during token/header acquisition (e.g. an offline
      // `onOnline` refetch failing the OAuth refresh fetch) surfaces here as a
      // bare `TypeError: Failed to fetch` / `Load failed`. The normal fetch path
      // tags this via `debugData.isBrowser`; this pre-request catch built the
      // RequestError without one, so `isBrowserNetworkFailure` never matched and
      // Sentry reported it at level:error (ADMIN-CD). Tag it the same way so
      // `isNonActionableRequestError` skips it for admin AND dashboard.
      const isBrowserNetworkTypeError =
        error instanceof TypeError &&
        "document" in globalThis &&
        (error.message.includes("Failed to fetch") ||
          error.message.includes("Load failed"));
      const requestError =
        error instanceof RequestError
          ? error
          : new RequestError(
              error instanceof Error
                ? error.message
                : `getBearerToken/getHeaders failed: ${String(error)}`,
              {
                cause: error,
                method,
                path,
                debugData: isBrowserNetworkTypeError
                  ? { isBrowser: true }
                  : undefined,
              },
            );
      onRequestError?.(requestError);
      logRequestError(context, requestError);
      throw requestError;
    }
  }

  const [firstBearerToken, dynamicHeaders] = await acquireAuthorization();
  let bearerToken = firstBearerToken;
  logRequestStart(context, method, path, options);

  let attempt = 0;
  const startTime = Date.now();

  while (true) {
    if (attempt > 0) [bearerToken] = await acquireAuthorization();
    const { requestObj, debugData } = buildRequest(
      host,
      basePath,
      method,
      path,
      input,
      contextHeaders,
      dynamicHeaders,
      bearerToken,
      credentials,
    );

    debugData["startTime"] = startTime;
    onRequestStart?.({ context, method, path, options, debugData });

    let response: Response;
    try {
      response = await (context.fetch ?? globalThis.fetch)(requestObj, {
        signal: effectiveSignal,
      });
    } catch (error) {
      if (isAbortError(error))
        throw raiseAbortError(context, error, { method, path, debugData });
      // Network/connection errors: retry if we still have attempts left
      if (attempt < RETRIES) {
        const delay = backoffMs(attempt, RETRY_DELAY_BASE_MS);
        attempt++;

        debugData["attempt"] = attempt;
        debugData["delay"] = delay;

        try {
          await sleep(delay, effectiveSignal);
        } catch (sleepError) {
          if (isAbortError(sleepError))
            throw raiseAbortError(context, sleepError, {
              method,
              path,
              debugData,
            });
          throw sleepError;
        }
        continue;
      }
      const requestError = new RequestTransportError(
        error instanceof Error
          ? error.message
          : `fetch failed: ${String(error)}`,
        { cause: error, debugData, method, path },
      );
      onRequestError?.(requestError);
      logRequestError(context, requestError);
      throw requestError;
    }

    const responseStatus = response.status;
    debugData["responseStatus"] = responseStatus;
    debugData["responseHeaders"] = JSON.stringify(
      Object.fromEntries(response.headers),
      null,
      2,
    );

    if (shouldRetry(responseStatus, method) && attempt < RETRIES) {
      const delay = getRetryDelay(attempt, response.headers.get("Retry-After"));
      attempt++;
      debugData["attempt"] = attempt;
      debugData["delay"] = delay;
      try {
        await sleep(delay, effectiveSignal);
      } catch (sleepError) {
        if (isAbortError(sleepError))
          throw raiseAbortError(context, sleepError, {
            method,
            path,
            debugData,
          });
        throw sleepError;
      }
      continue;
    }

    const isHandledClient = HANDLED_CLIENT_ERROR_CODE_SET.has(responseStatus);
    const isHandledServer = HANDLED_SERVER_ERROR_CODE_SET.has(responseStatus);
    const isStatusError =
      responseStatus < 200 || (responseStatus >= 300 && responseStatus < 400);

    if (isHandledClient || isHandledServer || isStatusError) {
      // Best-effort error body: null on parse failure is fine — the typed
      // error (ClientError/ServerError/StatusError) carries the status, and
      // consumers (auth handler, Sentry reporter) key off class + status.
      let responseBody: unknown = null;
      try {
        responseBody = await parseBody(response);
      } catch (error) {
        if (isAbortError(error))
          throw raiseAbortError(context, error, { method, path, debugData });
      }
      debugData["responseBody"] =
        responseBody === null
          ? null
          : typeof responseBody === "string"
            ? snip(responseBody, 200)
            : responseBody;

      const durationMs = Date.now() - startTime;
      const statusProps = {
        responseStatus,
        responseBody,
        debugData,
        durationMs,
        method,
        path,
      };
      const err = isHandledClient
        ? new ClientError(statusProps)
        : isHandledServer
          ? new ServerError(statusProps)
          : new StatusError(statusProps);
      onRequestError?.(err);
      logRequestError(context, err);
      throw err;
    }

    return { response, debugData, startTime };
  }
}

export function buildRequest(
  host: string,
  basePath: string,
  method: Method,
  path: string,
  input: RequestEndpoint["input"],
  contextHeaders: Record<string, string> | undefined,
  dynamicHeaders: Record<string, string>,
  bearerToken: string | null,
  credentials: "include" | "same-origin" | "omit",
): { requestObj: Request; debugData: { [k: string]: unknown } } {
  const isBrowser = "document" in globalThis;
  const url = buildURL(host, basePath, path, input);
  const [body, contentType] = getRequestBody(input);
  const cookieHeader = isBrowser ? undefined : getCookieString(input.cookie);
  const inputHeaders = getInputHeaders(input.header);

  const headers = {
    ...contextHeaders,
    ...dynamicHeaders,
    ...(bearerToken === null
      ? null
      : { Authorization: `Bearer ${bearerToken}` }),
    ...(contentType === undefined ? null : { "Content-Type": contentType }),
    ...(cookieHeader === undefined ? null : { Cookie: cookieHeader }),
    ...inputHeaders,
  };

  const bodyStr =
    body instanceof FormData
      ? "formdata<...>"
      : typeof body === "string"
        ? body
        : null;

  const debugData: { [k: string]: unknown } = {
    method,
    path,
    host,
    url: url.href,
    requestHeaders: JSON.stringify(headers, null, 2),
    requestBody: bodyStr === null ? null : snip(bodyStr, 200),
    bearerToken: bearerToken === null ? null : snip(bearerToken, 8),
    input: JSON.stringify(input, null, 2),
    credentials,
    isBrowser,
  };

  const requestObj = new globalThis.Request(url.href, {
    method: method.toUpperCase(),
    headers,
    credentials,
    body,
  });

  return { requestObj, debugData };
}

const RETRIES = 3;
const RETRY_DELAY_BASE_MS = 250;
const shouldRetry = (
  status: number,
  method: RequestEndpoint["method"],
): boolean => {
  if (!RETRIED_STATUS_CODE_SET.has(status)) return false;
  return (
    status === 429 ||
    method === "get" ||
    method === "options" ||
    method === "head"
  );
};

function backoffMs(attempt: number, base: number): number {
  // Exponential backoff with jitter: base * 2^attempt + random(0..base)
  const exp = base * Math.pow(2, attempt);
  return exp + Math.floor(Math.random() * base);
}

function getRetryDelay(attempt: number, retryHeader: string | null): number {
  if (retryHeader !== null) {
    const seconds = Number(retryHeader);
    if (!Number.isNaN(seconds)) return seconds * 1000;
    const date = Date.parse(retryHeader);
    if (!Number.isNaN(date)) return Math.max(0, date - Date.now());
  }
  return backoffMs(attempt, RETRY_DELAY_BASE_MS);
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

function sleep(ms: number, signal?: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted === true) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener("abort", () => {
      clearTimeout(timer);
      reject(new DOMException("Aborted", "AbortError"));
    });
  });
}

const RETRIED_STATUS_CODES = [429, 502, 503, 504] as const;
const RETRIED_STATUS_CODE_SET = new Set<number>(RETRIED_STATUS_CODES);

// these error codes are handled by the stack and will throw a ClientError, they will never show up in the request output.
const HANDLED_CLIENT_ERROR_CODES = [400, 401, 403, 404, 409, 422, 429] as const;
const HANDLED_CLIENT_ERROR_CODE_SET = new Set<number>(
  HANDLED_CLIENT_ERROR_CODES,
);
type HandledClientErrorCode = (typeof HANDLED_CLIENT_ERROR_CODES)[number];

// these error codes are handled by the stack and will throw a ServerError, they will never show up in the request output.
const HANDLED_SERVER_ERROR_CODES = [
  500, 501, 502, 503, 504, 505, 506, 507, 508, 510, 511,
] as const;
const HANDLED_SERVER_ERROR_CODE_SET = new Set<number>(
  HANDLED_SERVER_ERROR_CODES,
);
type HandledServerErrorCode = (typeof HANDLED_SERVER_ERROR_CODES)[number];

interface RequestErrorProps {
  debugData?: Record<string, unknown>;
  cause?: unknown;
  method: Method;
  path: string;
}

export class RequestError extends Error {
  debugData?: Record<string, unknown>;
  method: Method;
  path: string;
  constructor(
    message: string,
    { debugData, cause, method, path }: RequestErrorProps,
  ) {
    super(message, { cause });
    this.name = "RequestError";
    this.debugData = debugData;
    this.method = method;
    this.path = path;
  }
  get debugMessage(): string {
    return `${this.method.toUpperCase()}:${this.path} -> "${this.message}"`;
  }
  /**
   * What a LOG may say about this error, as opposed to what a person may read.
   *
   * `message` is the user-facing text — 224 call sites render it through
   * `getErrorMessage` — and for a `StatusError` it is composed out of the
   * upstream response body, which can carry a token. The two audiences want
   * different strings, so they are two properties rather than one compromise
   * (SEC-02 in docs/security-roadmap.md).
   *
   * A first-class discriminant, in the manner of `isCancellation` below, so the
   * serialiser reads a property instead of probing a shape. The base answers
   * `message`: an error this app raised itself composed its own text and is safe
   * to repeat. Only the subclass that builds its text out of someone else's
   * bytes overrides it.
   */
  get logMessage(): string {
    return this.message;
  }
  /**
   * Whether this error is a cancellation (the request was aborted) rather than
   * a real failure with a verdict. Read at the query boundary so a cancelled
   * request is never surfaced as an error state — see
   * `shared/query.ts` createQueryResult. A first-class discriminant rather than
   * a runtime `instanceof` probe.
   */
  get isCancellation(): boolean {
    return false;
  }
}

/**
 * The fetch itself failed on every attempt (connection refused, DNS, reset):
 * no HTTP response exists. Distinct from `StatusError` so a server that proxies
 * another service can answer 502 instead of reading the failure as its own.
 */
export class RequestTransportError extends RequestError {
  constructor(message: string, props: RequestErrorProps) {
    super(message, props);
    this.name = "RequestTransportError";
  }
}

interface StatusErrorProps extends RequestErrorProps {
  responseStatus: number;
  responseBody: unknown;
}

function errorFields<S extends z.ZodRawShape>(shape: S) {
  return z.preprocess((value) => {
    if (!Array.isArray(value)) return value;
    const fields = {};
    Object.setPrototypeOf(fields, value);
    return fields;
  }, z.object(shape));
}

const messageField = errorFields({ message: z.string() });
const causeField = errorFields({ cause: z.unknown() });
const codeField = errorFields({ code: z.string() });
const errorInfoField = errorFields({ errorInfo: z.unknown() });
const errnoField = errorFields({ errno: z.union([z.string(), z.number()]) });
const nameField = errorFields({ name: z.string() });
const nestedMessageFields = errorFields({
  responseBody: z.unknown().optional(),
  response: z.unknown().optional(),
  data: z.unknown().optional(),
});

function extractUserMessage(body: unknown): string | null {
  if (typeof body === "string" && body.length > 0)
    return body.length > 500 ? `${body.slice(0, 500)}…` : body;
  if (typeof body === "object" && body !== null) {
    const parsed = messageField.safeParse(body);
    if (parsed.success) return parsed.data.message;
  }
  return null;
}

export class StatusError extends RequestError {
  responseStatus: number;
  responseBody: unknown;

  constructor(props: StatusErrorProps) {
    const message =
      extractUserMessage(props.responseBody) ??
      `Request failed with status ${props.responseStatus}`;
    super(message, props);
    this.name = "StatusError";
    this.responseStatus = props.responseStatus;
    this.responseBody = props.responseBody;
  }

  override get debugMessage(): string {
    return `${super.debugMessage} [${this.responseStatus}]`;
  }

  /**
   * The status, the route and nothing the upstream said.
   *
   * `message` here is `extractUserMessage(responseBody)` — a string body becomes
   * it verbatim, and an object carrying `message` donates that value. Useful to
   * show a person, unsafe to write down: the body belongs to a third party and
   * an OAuth or token endpoint puts credentials in it. The method and path are
   * ours, so they stay.
   */
  override get logMessage(): string {
    return `${this.method.toUpperCase()} ${this.path} failed with status ${this.responseStatus}`;
  }
}

export class ServerError extends StatusError {
  constructor(props: StatusErrorProps) {
    super(props);
    this.name = "ServerError";
  }
}

export class ClientError extends StatusError {
  constructor(props: StatusErrorProps) {
    super(props);
    this.name = "ClientError";
  }
}

/**
 * Raised when the request was aborted — either because the caller's `signal`
 * fired, or because the default 60 s deadline fired. Wraps the raw
 * `DOMException("AbortError")` so the request library only ever rejects with
 * `RequestError` subclasses; without this, raw DOMExceptions would leak past
 * `onRequestError` and `isNonActionableRequestError`, ending up reported to
 * Sentry by api-side fire-and-forget `.catch(logger.unknownError)` callers
 * even though aborts are infrastructure events (timeouts, navigation), not
 * bugs.
 */
export class RequestAbortError extends RequestError {
  constructor(props: RequestErrorProps) {
    super("Request aborted", props);
    this.name = "RequestAbortError";
  }
  override get isCancellation(): boolean {
    return true;
  }
}

/**
 * Raised by a frontend `getBearerToken` when it cannot produce a bearer token
 * for a private request — the local auth session isn't readable yet, a token
 * refresh failed transiently (flaky network, Firebase internal error), or the
 * persisted session was evicted (e.g. WKWebView storage pressure under iOS, so
 * `currentUser` is momentarily null after a native-triggered webview reload).
 *
 * This is a CLIENT-side "no token right now" condition, NOT a server rejection.
 * It is deliberately a plain `RequestError` (never a `ClientError`/401) so the
 * auth-error-handler does NOT treat it as session expiry and log the user out:
 * only a 401 returned by the actual API server expires the session. The
 * frontend's Firebase-auth route guard owns the redirect when the user is
 * genuinely signed out. `isNonActionableRequestError` skips it so transient
 * token hiccups don't spam Sentry.
 */
export class AuthTokenUnavailableError extends RequestError {
  constructor(props: RequestErrorProps) {
    super("Auth token unavailable", props);
    this.name = "AuthTokenUnavailableError";
  }
}

function raiseAbortError(
  context: RequestContext,
  cause: unknown,
  props: { method: Method; path: string; debugData?: Record<string, unknown> },
): RequestAbortError {
  const abortError = new RequestAbortError({ ...props, cause });
  context.onRequestError?.(abortError);
  logRequestError(context, abortError);
  return abortError;
}

export function castValue(value: StringCastable): string {
  return value === null
    ? NULL_STR
    : value === true
      ? TRUE_STR
      : value === false
        ? FALSE_STR
        : value instanceof Date
          ? value.toISOString()
          : typeof value === "number"
            ? value.toString()
            : value;
}

export function buildPath(
  path: string | null,
  routeParams: Partial<Record<string, StringCastable>>,
): string {
  if (path === null) return "";
  const parts: string[] = [];
  for (const pathPart of path.split("/")) {
    if (!pathPart.startsWith(":")) {
      parts.push(pathPart);
      continue;
    }

    const name = pathPart.slice(1);
    if (!(name in routeParams) || routeParams[name] === undefined)
      throw new Error(`Missing route parameter ${name}`);
    parts.push(encodeURIComponent(castValue(routeParams[name])));
  }

  return mergePathChunks(parts);
}

export function getInputHeaders(
  inputHeaders: RequestEndpoint["input"]["header"],
): Record<string, string> | undefined {
  if (!inputHeaders) return undefined;
  const headers: Record<string, string> = {};
  for (const key in inputHeaders) {
    const value = inputHeaders[key];
    if (value === undefined) continue;
    headers[key] = castValue(value);
  }
  return headers;
}

export function mergePathChunks(chunks: (string | null | undefined)[]): string {
  return chunks
    .filter(Boolean)
    .map((chunk) => chunk?.replace(/^\//, "").replace(/\/$/, "") ?? "")
    .filter(Boolean)
    .join("/");
}

export function buildURL(
  host: string,
  basePath: string,
  path: string,
  input: RequestEndpoint["input"],
): URL {
  const { param, query } = input;

  // Some configured hosts already include the basePath suffix; strip it so we
  // don't double it when composing the URL.
  if (basePath !== "" && host.endsWith(basePath)) basePath = "";

  const url = new URL(
    mergePathChunks([
      host,
      basePath,
      param === undefined ? path : buildPath(path, param),
    ]),
  );

  if (query)
    for (const key of Object.keys(query).sort()) {
      const value = query[key];
      if (value === undefined) continue;
      if (Array.isArray(value)) {
        if (value.length === 0) {
          url.searchParams.append(key, EMPTY_QUERY_PARAM_ARRAY_VALUE);
        } else {
          for (const v of value) url.searchParams.append(key, castValue(v));
        }
      } else {
        url.searchParams.append(key, castValue(value));
      }
    }

  return url;
}

async function parseBody(res: Response): Promise<unknown> {
  if (res.status === 204 || res.status === 205) return null;
  const type = res.headers.get("content-type");
  if (type === null) return null;

  if (type.includes("json")) return res.json();
  if (type.includes("text")) return res.text();
  if (
    type.startsWith("application/") ||
    type.startsWith("image/") ||
    type.startsWith("audio/")
  )
    return res.blob();
  return null;
}

type RequestBody = string | FormData | undefined;
type RequestContentType = string | undefined;
export function getRequestBody({
  json,
  form,
}: RequestEndpoint["input"]): [RequestBody, RequestContentType] {
  let formData: FormData | undefined;
  if (form) {
    formData = form instanceof FormData ? form : new FormData();
    if (!(form instanceof FormData)) {
      for (const k in form) {
        const v = form[k];
        if (v === undefined || v === null) continue;
        if (Array.isArray(v)) {
          if (v.length === 0) {
            formData.append(k, EMPTY_QUERY_PARAM_ARRAY_VALUE);
          } else
            for (const vv of v)
              if (vv instanceof File) {
                formData.append(k, vv, vv.name);
              } else {
                formData.append(k, castValue(vv));
              }
        } else {
          if (v instanceof File) {
            formData.append(k, v, v.name);
          } else {
            formData.append(k, castValue(v));
          }
        }
      }
    }
  } else {
    formData = undefined;
  }
  const jsonData = json === undefined ? undefined : JSON.stringify(json);
  if (jsonData !== undefined && formData !== undefined)
    throw new Error("json and form data cannot be used together");
  const body = jsonData ?? formData;

  // Crucial: Return undefined for content-type if using FormData
  // so the browser sets the boundary.
  const contentType =
    json === undefined ? undefined : "application/json; charset=utf-8";

  return [body, contentType] as const;
}

function getCookieString(
  cookies: RequestEndpoint["input"]["cookie"],
): string | undefined {
  if (!cookies) return undefined;
  const entries = Object.entries(cookies);
  if (entries.length === 0) return undefined;

  return entries
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
    )
    .join("; ");
}

type IsEmptyObject<T> = [T] extends [object]
  ? keyof T extends never
    ? true
    : false
  : false;

function snip(str: string, keepLen: number): string {
  const keepSegmentLen = Math.floor(keepLen / 2);
  if (str.length < keepSegmentLen * 3) return str;
  return `[${str.slice(0, keepSegmentLen)}...${str.slice(-keepSegmentLen)}](${str.length} chars)`;
}

const Reset = "\u001B[0m";
const FgRed = "\u001B[31m";
const FgGreen = "\u001B[32m";
const FgYellow = "\u001B[33m";
const FgBlue = "\u001B[34m";
const FgMagenta = "\u001B[35m";
const FgCyan = "\u001B[36m";
const FgLightGray = "\u001B[37m";

function methodColorized(method: Method, outgoing: boolean): string {
  if (outgoing) return `${FgLightGray}${method}${Reset}`;
  switch (method) {
    case "head":
    case "options": {
      return method;
    }
    case "get": {
      return `${FgCyan}${method}${Reset}`;
    }
    case "post": {
      return `${FgBlue}${method}${Reset}`;
    }
    case "patch":
    case "put": {
      return `${FgYellow}${method}${Reset}`;
    }
    case "delete": {
      return `${FgRed}${method}${Reset}`;
    }
    default: {
      throw new Error(method satisfies never);
    }
  }
}

function statusColorized(status: number, message?: string): string {
  if (status < 300)
    return `[${FgGreen}${status}${Reset}${message === undefined ? "" : `:${message}`}]`;
  if (status >= 300 && status < 500)
    return `[${FgYellow}${status}${Reset}${message === undefined ? "" : `:${message}`}]`;

  return `[${FgRed}${status}${Reset}${message === undefined ? "" : `:${message}`}]`;
}

const DEFAULT_LOG_PREFIX = "[req]";

function colorizedLogPrefix(context: RequestContext): string {
  return `[${FgMagenta}${context.logPrefix ?? DEFAULT_LOG_PREFIX}${Reset}]`;
}

/**
 * True when `error` is a non-actionable failure — page teardown parse race,
 * offline / CORS-stripped browser network failure, or an abort/timeout
 * (caller signal or the library's default 60 s deadline). None of these are
 * real backend or network bugs worth alerting on. Sentry reporters should
 * skip these by default.
 */
export function isNonActionableRequestError(error: RequestError): boolean {
  return (
    error instanceof RequestAbortError ||
    error instanceof AuthTokenUnavailableError ||
    isLikelyBrowserTeardownParseFailure(error) ||
    isBrowserNetworkFailure(error)
  );
}

function isLikelyBrowserTeardownParseFailure(error: RequestError): boolean {
  if (error instanceof StatusError) return false;
  if (!error.message.includes("cannot parse response body")) return false;
  if (
    !error.message.includes("Failed to fetch") &&
    !error.message.includes("Load failed")
  )
    return false;

  const debugData = error.debugData;
  if (debugData === undefined) return false;

  if (debugData["isBrowser"] !== true) return false;
  if (debugData["responseStatus"] !== 200) return false;
  if (debugData["method"] !== "get") return false;

  const responseContentType = debugData["responseContentType"];
  return (
    typeof responseContentType === "string" &&
    responseContentType.toLowerCase().includes("json")
  );
}

function isBrowserNetworkFailure(error: RequestError): boolean {
  if (error instanceof StatusError) return false;
  const debugData = error.debugData;
  if (debugData === undefined) return false;
  return debugData["isBrowser"] === true && !("responseStatus" in debugData);
}

function logRequestError(context: RequestContext, error: RequestError) {
  const logger = context.logger;
  if (logger === undefined) return;
  const prefix = context.logPrefix ?? DEFAULT_LOG_PREFIX;
  // Non-actionable errors (aborts/timeouts, browser teardown, browser
  // network failures): log as warn so they stay visible locally without
  // being treated as bugs.
  if (isNonActionableRequestError(error)) {
    logger.warn(`${prefix} ${error.debugMessage}`, error);
    return;
  }
  const logMethod = error instanceof ClientError ? "warn" : "error";
  logger[logMethod](`${prefix} ${error.debugMessage}`, error);
}

function logRequestStart(
  context: RequestContext,
  method: RequestEndpoint["method"],
  path: RequestEndpoint["path"],
  options: RequestArgs<RequestEndpoint>[0],
) {
  const logger = context.logger;
  if (logger === undefined) return;
  logger.debug(
    `${colorizedLogPrefix(context)} ${methodColorized(method, true)} ${FgLightGray}${path}...${Reset}`,
    options,
  );
}

function logRequestSuccess(
  context: RequestContext,
  method: RequestEndpoint["method"],
  path: RequestEndpoint["path"],
  output: RequestOutput<RequestEndpoint>,
  durationMs: number,
) {
  const logger = context.logger;
  if (logger === undefined) return;
  const prefix = `${colorizedLogPrefix(context)} ${methodColorized(method, false)} ${path}`;

  if (
    typeof output.body === "string" ||
    typeof output.body === "number" ||
    typeof output.body === "boolean"
  ) {
    logger.debug(
      `${prefix} ${statusColorized(output.status, snip(JSON.stringify(output.body), 200))} ${durationMs}ms`,
    );
  } else {
    logger.debug(
      `${prefix} ${statusColorized(output.status)} ${FgLightGray}${durationMs}ms${Reset}`,
      output.body,
    );
  }
}

export type JsonFileInput = {
  name: string;
  type: string;
  size: number; // File size in bytes
  content: string; // Base64 string
  lastModified: number;
};

export async function toJsonFile(file: File): Promise<JsonFileInput> {
  return {
    name: file.name,
    type: file.type,
    size: file.size,
    lastModified: file.lastModified,
    content: await fileToBase64(file),
  };
}

/**
 * Base64-encode a File in any environment. Browsers go through
 * FileReader.readAsDataURL — the engine's native, asynchronous encoder —
 * because a manual byte loop + btoa blocks the main thread for hundreds of
 * milliseconds on multi-MB files (dictation takes run up to ~10MB). Node keeps
 * the Buffer path.
 */
async function fileToBase64(file: File): Promise<string> {
  if (typeof FileReader === "undefined") {
    return Buffer.from(await file.arrayBuffer()).toString("base64"); // Node.js
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("readAsDataURL produced a non-string result"));
        return;
      }
      // Strip the "data:<mime>;base64," prefix — the jsonFile schema accepts
      // either form, but the bare payload keeps the wire smaller.
      const separator = result.indexOf(",");
      if (separator === -1) {
        reject(new Error("readAsDataURL produced no data-URL separator"));
        return;
      }
      resolve(result.slice(separator + 1));
    });
    reader.addEventListener("error", () => {
      reject(new Error("File read failed"));
    });
    reader.readAsDataURL(file);
  });
}

/**
 * Traverse `cause` chain (bounded) to find the first usable error code.
 */
export function getErrorCodeDeep(e: unknown, maxDepth = 3): string | null {
  let cur: unknown = e;
  for (let depth = 0; depth < maxDepth; depth++) {
    const code = getErrorCode(cur);
    if (code !== null) return code;
    const next = getCause(cur);
    if (next === null) break;
    cur = next;
  }
  return null;
}

function getCause(e: unknown): unknown {
  if (typeof e !== "object" || e === null) return null;
  const parsed = causeField.safeParse(e);
  if (!parsed.success) return null;
  const cause = parsed.data.cause;
  return cause === null || cause === undefined ? null : cause;
}

function errorText(e: unknown): string {
  if (e instanceof Error) return `${e.name}: ${e.message}\n${e.stack ?? ""}`;
  try {
    if (typeof e === "string") return e;
    return JSON.stringify(e);
  } catch {
    try {
      return String(e);
    } catch {
      return "";
    }
  }
}

export function errorTextDeep(e: unknown, maxDepth = 3): string {
  const parts: string[] = [];
  let cur: unknown = e;

  for (let depth = 0; depth < maxDepth; depth++) {
    const t = errorText(cur);
    if (t) parts.push(t);
    const next = getCause(cur);
    if (next === null) break;
    cur = next;
  }

  return parts.join("\nCaused by: ");
}

function getErrorCode(e: unknown): string | null {
  if (typeof e !== "object" || e === null) return null;
  const code = codeField.safeParse(e);
  if (code.success) return code.data.code;

  const errorInfo = errorInfoField.safeParse(e);
  if (errorInfo.success) {
    const nestedCode = codeField.safeParse(errorInfo.data.errorInfo);
    if (nestedCode.success) return nestedCode.data.code;
  }

  const errno = errnoField.safeParse(e);
  if (errno.success) return String(errno.data.errno);

  const name = nameField.safeParse(e);
  if (name.success && /^[A-Z0-9_-]+$/.test(name.data.name))
    return name.data.name;

  return null;
}

function extractNestedUserMessage(value: unknown, maxDepth = 3): string | null {
  if (maxDepth < 0) return null;

  const directMessage = extractUserMessage(value);
  if (directMessage !== null) return directMessage;

  if (typeof value !== "object" || value === null) return null;

  const parsed = nestedMessageFields.safeParse(value);
  if (!parsed.success) return null;
  const obj = parsed.data;
  return (
    extractNestedUserMessage(obj["responseBody"], maxDepth - 1) ??
    extractNestedUserMessage(obj["response"], maxDepth - 1) ??
    extractNestedUserMessage(obj["data"], maxDepth - 1)
  );
}

export function getErrorMessage(error: unknown): string {
  if (error instanceof RequestError) return error.message;
  // best effort
  return extractNestedUserMessage(error) ?? String(error);
}
